import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, Filter, X, MapPin, Calendar, Maximize2 } from 'lucide-react';
import './HeroPortfolio.css';
import Header from "./Header";
import Footer from "./Footer";
import Chatbot from '../components/Chatbot';
import { useNavigate } from 'react-router-dom';

// Helper to load dynamic user uploaded projects
const getCombinedProjects = (defaultProjects) => {
  try {
    const custom = JSON.parse(localStorage.getItem('sakae_custom_projects') || '[]');
    return [...custom, ...defaultProjects];
  } catch (e) {
    return defaultProjects;
  }
};

// All images are now locally optimized WebP assets.
// When Cloudflare R2 is ready, change this base URL to https://images.sakae.in
const CDN_BASE = '';
const cdnImage = (relativePath) => {
  // Map to local WebP asset paths
  const webpPath = relativePath.replace(/\.(png|jpe?g)$/i, '.webp');
  return require(`../assets/${webpPath}`);
};

// 3D Tilt hook
const useTilt = () => {
  const ref = useRef(null);
  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transform = `perspective(900px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg) scale(1.02)`;
    el.style.boxShadow = `${-x * 16}px ${y * 16}px 48px rgba(0,0,0,0.22), 0 0 0 1px rgba(212,175,55,0.25)`;
  }, []);
  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) scale(1)';
    el.style.boxShadow = '';
    el.style.transition = 'transform 0.6s ease, box-shadow 0.6s ease';
  }, []);
  return { ref, onMouseMove, onMouseLeave };
};

const ProjectCard = ({ project, onOpen }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt();
  return (
    <div
      ref={ref}
      className="project-card"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={() => onOpen(project)}
    >
      <div className="card-image">
        <img
          src={project.image}
          alt={project.title}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.target.onerror = null; }}
        />
        <div className="overlay">
          <button onClick={(e) => { e.stopPropagation(); onOpen(project); }}>
            <Eye size={15} /> View Project
          </button>
        </div>
        <span className={`status-badge ${project.status === 'Completed' ? 'completed' : 'ongoing'}`}>
          {project.status}
        </span>
      </div>
      <div className="card-info">
        <h3>{project.title}</h3>
        <span className="category-badge">
          {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
        </span>
        <p>{project.description.substring(0, 110)}...</p>
      </div>
      <div className="card-meta">
        <span><Calendar size={12} />{project.year}</span>
        <span><MapPin size={12} />{project.area}</span>
      </div>
    </div>
  );
};

const HeroPortfolio = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = getCombinedProjects([
    // Existing Project 1
    // {
    //   id: 5,
    //   title: 'Office Architecture by Sakae Studio',
    //   category: 'Commercial',
    //   architect: 'Sameed Ahmed Khan',
    //   projectType: 'Office Building',
    //   strategy: 'Comprehensive Design',
    //   date: 'October 2024',
    //   status: 'Ongoing',
    //   image: cdnImage('OfficeComplex/o1.webp'),
    //   description: 'A seamless dialogue between nature and structure, redefining the modern workspace experience with a central open courtyard, bold glass façade, and vertical greens.',
    //   details: {
    //     overview: 'Envisioned as a statement of contemporary architecture and refined functionality, this office complex by Sakae Studio embraces a central open courtyard that draws in natural light and greenery to create calm, breathable interiors.',
    //     architecture: [
    //       'Bold glass façade framed with copper-toned metal fins',
    //       'Vertical greens integrated throughout the facade',
    //       'Central open courtyard for natural light and ventilation',
    //       'Landscaped forecourt with water feature',
    //       'Metal lattice panels creating visual rhythm',
    //       'Philosophy of blending opulence with restraint'
    //     ],
    //     sitePlanning: [
    //       'Efficient circulation and movement patterns',
    //       'Clear zoning for parking and pedestrian areas',
    //       'Landscape integration throughout the site',
    //       'Carefully choreographed approach sequence',
    //       'Visual transparency and openness',
    //       'Sense of grandeur with functional simplicity'
    //     ],
    //     interiorSpaces: [
    //       'Calm and breathable working environments',
    //       'Natural light throughout the building',
    //       'Visual openness centered around courtyard',
    //       'Inspiring and efficient workspace design',
    //       'Clarity in circulation and ease of access',
    //       'Spatial comfort and modern aesthetics'
    //     ],
    //     designPhilosophy: [
    //       'Crafted modernism with timeless elegance',
    //       'Balance between modern aesthetics and sustainability',
    //       'Every detail speaks of refined craftsmanship',
    //       'Facade articulation with geometric precision',
    //       'Landscape geometry integration',
    //       'Embodiment of Opulent Architecture'
    //     ]
    //   },
    //   client: 'Corporate Client',
    //   year: '2024',
    //   area: '15,000 sq ft',
    //   gallery: Array.from({ length: 16 }, (_, i) =>
    //     cdnImage(`OfficeComplex/o${i + 1}.webp`)
    //   ),
    //   designHighlights: [
    //     'Central landscaped courtyard design',
    //     'Bold glass façade with metal lattice',
    //     'Vertical green integration',
    //     'Natural light optimization',
    //     'Efficient circulation planning',
    //     'Contemporary sustainable architecture'
    //   ]
    // },
    {
      id: 1,
      title: '3BHK Luxury Apartment',
      category: 'residential',
      architect: 'Sameed Ahmed Khan',
      projectType: '3BHK Apartment',
      strategy: 'Warm, Comfortable & Functional Elegance',
      date: 'February 2025',
      status: 'Completed',
      image: cdnImage('apartment/a1.webp'),
      description: 'A sophisticated 3BHK apartment featuring modern amenities, luxurious finishes, and thoughtful space planning that combines elegance with functionality.',
      details: {
        overview: 'This residential project showcases our ability to create modern, functionally elegant spaces through thoughtful planning and sophisticated material choices.',
        livingDining: [
          'Open-plan layout with light walls and marble floors',
          'Varied lighting scheme for bright, inviting atmosphere',
          'Elegant furnishings with rich rugs and wooden accents',
          'Integrated entertainment system with hidden wiring',
          'Custom storage solutions throughout',
          'Contemporary art pieces as focal points'
        ],
        bedrooms: [
          'Master bedroom with dark, lit headboard feature wall',
          'Black glass wardrobes with interior lighting',
          'Integrated study areas with warm wood tones',
          'Customized storage solutions for each family member',
          'Sleek workspace with extensive shelving',
          'Minimalist vanity areas with smart storage'
        ],
        kitchen: [
          'Black marble countertops with waterfall edge',
          'Vibrant green and white high-gloss cabinetry',
          'Integrated high-end appliances',
          'Optimized storage with pull-out systems',
          'Abundant natural light from skylights',
          'Practical yet visually striking design'
        ]
      },
      client: 'Private Client',
      year: '2025',
      area: '1200 sq ft',
      gallery: Array.from({ length: 14 }, (_, i) =>
        cdnImage(`apartment/a${i + 1}.webp`)
      ),
      designHighlights: [
        'Functional elegance with premium materials',
        'Custom storage solutions throughout',
        'Lighting-designed atmosphere variations',
        'Smart home technology integration',
        'Bespoke furniture pieces',
        'Material contrasts for visual interest'
      ]
    },
    // New Project 2 - DD Residence
    {
      id: 2,
      title: 'DD Residence',
      category: 'residential',
      architect: 'Sameed Ahmed Khan',
      projectType: 'Villa (Multi-story Residential Buildings)',
      strategy: 'Holistic Development',
      date: 'December 2024',
      status: 'Completed',
      image: cdnImage('DDResidence/b1.webp'),
      description: 'A comprehensive multi-story residential villa showcasing thoughtful architectural design and modern living spaces across multiple levels with integrated outdoor spaces.',
      details: {
        overview: 'DD Residence by SAKAE Opulent Architecture demonstrates a comprehensive approach to multi-story residential living with detailed floor plans and integrated outdoor spaces.',
        groundFloor: [
          'Welcoming foyer with distinct living and dining areas',
          'Functional kitchen with utility space',
          'One bedroom with two toilets',
          'Outdoor seating and set-back areas',
          'Enhanced ground-level experience design'
        ],
        firstFloor: [
          'Spacious dining/living hall',
          'Guest bedroom with balcony access',
          'Two additional bedrooms with three toilets',
          'Central lift and staircase connectivity',
          'Multiple balconies for outdoor access'
        ],
        secondFloor: [
          'Consistent dining/living hall layout',
          'Guest bedroom with private balcony',
          'Two more bedrooms with three toilets',
          'Maintained flow and outdoor space access',
          'Functional layout continuation'
        ],
        exteriorDesign: [
          'Multi-story facade with distinct aesthetic',
          'White and reddish-brown architectural elements',
          'Rooftop terrace with lift room access',
          'Beautifully landscaped outdoor areas',
          'Green lawn with paved sections and garden beds',
          'Designed seating and gazebo structures'
        ]
      },
      client: 'Private Client',
      year: '2024',
      area: '3500 sq ft',
      gallery: Array.from({ length: 9 }, (_, i) =>
        cdnImage(`DDResidence/b${i + 1}.webp`)
      ),
      designHighlights: [
        'Multi-story comprehensive design',
        'Integrated outdoor spaces',
        'Rooftop terrace with landscaping',
        'Central lift and staircase system',
        'Multiple balconies and outdoor access',
        'Cohesive aesthetic throughout levels'
      ]
    },
    // New Project 3 - Skyline Crest Apartments
    {
      id: 3,
      title: 'Skyline Crest Apartments',
      category: 'commercial',
      architect: 'Sameed Ahmed Khan',
      projectType: 'High-Rise Residential Development',
      strategy: 'Comprehensive Design',
      date: 'February 2024',
      status: 'Completed',
      image: cdnImage('SkylineCrest/c1.webp'),
      description: 'A comprehensive high-rise residential development featuring master planning with extensive amenities, detailed unit designs, and complete infrastructure for modern community living.',
      details: {
        overview: 'Skyline Crest Apartments showcases comprehensive architectural and interior design expertise from conceptualization to detailed execution in high-rise residential development.',
        masterPlanning: [
          'Large-scale residential development with multiple blocks (A1-A7, B1-B5)',
          'Wide roads (8M and 10M) with strategic planning',
          'Community amenities including parks and play areas',
          'Amphitheaters and clubhouses with social spaces',
          'Swimming pools and comprehensive sports complexes',
          'Commercial zones and essential infrastructure integration'
        ],
        unitDesign: [
          '2BHK units with living area, kitchen, dining space, and balconies',
          '3BHK units with family living/dining and drawing rooms',
          'Bright living spaces with light-colored walls and marble flooring',
          'Sophisticated lighting with cove lighting and elegant chandeliers',
          'Plush furnishings with dark ornate rugs and wooden accents',
          'Integrated entertainment units and strategic hidden storage'
        ],
        bedrooms: [
          'Master bedrooms with dark textured headboard walls',
          'Integrated lighting and striking floor-to-ceiling wardrobes',
          'Study areas with shelving and task lighting',
          'Warm wooden tones with ample storage solutions',
          'Sleek and minimalist vanity areas'
        ],
        kitchens: [
          'Modern and highly functional design approach',
          'Black marble countertops with olive green base cabinets',
          'Crisp white upper cabinets for visual contrast',
          'Optimized storage and abundant natural light',
          'Striking color palettes for visual appeal'
        ],
        amenities: [
          'Multi-level club house for social and recreational activities',
          'Kindergarten with functional child-friendly environment',
          'Restaurant with well-laid-out dining and kitchen areas',
          'Sports complexes (volleyball, tennis, basketball, cricket pitch)',
          'Swimming pools and comprehensive recreational facilities'
        ]
      },
      client: 'Real Estate Developer',
      year: '2024',
      area: '25 acres',
      gallery: Array.from({ length: 8 }, (_, i) =>
        cdnImage(`SkylineCrest/c${i + 1}.webp`)
      ),
      designHighlights: [
        'Master planning with extensive amenities',
        'Multi-block residential development',
        'Comprehensive sports and recreation facilities',
        'Modern 2BHK and 3BHK unit designs',
        'Club house and community spaces',
        'Integrated electrical and MEP planning'
      ]
    },
    // New Project 4 - Odisha Enclave
    {
      id: 4,
      title: 'Odisha Enclave',
      category: 'residential',
      architect: 'Sameed Ahmed Khan',
      projectType: 'Apartment',
      strategy: 'Comprehensive Design',
      date: 'May 2025',
      status: 'Completed',
      image: cdnImage('OdishaEnclave/d1.webp'),
      description: 'Innovative interior design focusing on creating modern, sophisticated, and inviting living spaces with a blend of contemporary aesthetics and functional elegance.',
      details: {
        overview: 'Odisha Enclave showcases innovative interior design, emphasizing a blend of contemporary aesthetics with functional elegance through detailed mood boards and high-quality renders.',
        conceptualization: [
          'Comprehensive mood board capturing space essence and ambiance',
          'Sophisticated color palettes with neutrals, greys, and whites',
          'Warm wood tones with subtle accents of black and muted blues',
          'Luxurious textures including marble, polished concrete, and light wood',
          'Modern furniture designs with statement lighting fixtures',
          'Abstract art pieces for contemporary elegance'
        ],
        livingSpaces: [
          'Open-plan living and dining with spacious layouts',
          'Elegant furnishings with light neutral-toned sofas',
          'Modern coffee tables with marble tops and sleek designs',
          'Contemporary dining tables with comfortable upholstered chairs',
          'Sophisticated multi-layered lighting with recessed spotlights',
          'Decorative pendant lamps enhancing ambiance'
        ],
        designElements: [
          'Feature walls with vertical fluting and subtle paneling',
          'Wooden flooring and accents bringing warmth',
          'Light wall colors creating harmonious balance',
          'Strategically placed modern artwork and decorative elements',
          'Wall-mounted televisions in stylish media consoles',
          'Integrated shelving units providing entertainment and storage'
        ],
        bedrooms: [
          'Continuation of modern aesthetic into private spaces',
          'Emphasis on comfort and sophisticated simplicity',
          'Large integrated display units for design continuity',
          'Luxurious and functional bedroom environments',
          'Sophisticated storage solutions integration'
        ]
      },
      client: 'Private Client',
      year: '2025',
      area: '1800 sq ft',
      gallery: Array.from({ length: 2 }, (_, i) =>
        cdnImage(`OdishaEnclave/d${i + 1}.webp`)
      ),
      designHighlights: [
        'Contemporary aesthetics with functional elegance',
        'Sophisticated color palettes and material selection',
        'Multi-layered lighting design',
        'Integrated entertainment and storage solutions',
        'Modern furniture and statement pieces',
        'Cohesive design continuity throughout spaces'
      ]
    },
    // Project - Trudent Dental Clinic (Version 1)
{
  id: 6,
  title: 'Trudent Dental Clinic – Healthcare Interior Design',
  category: 'Healthcare',
  architect: 'Sameed Ahmed Khan',
  projectType: 'Dental Clinic',
  strategy: 'Comprehensive Interior Design',
  date: 'September 2025',
  status: 'Completed',
  image: cdnImage('trudent_v1/x1.webp'),
  description:
    'A modern and calming dental clinic interior designed to create a warm, patient-friendly atmosphere using natural materials, soft lighting, and seamless functionality.',
  details: {
    overview:
      'Located in Gandhi Complex, Mehdipatnam, Trudent Dental Hospital is designed with a focus on calmness, modern aesthetics, and functional efficiency. Warm wooden textures, subtle greenery, and soft lighting create a welcoming patient experience.',
    designConcept: [
      'Warm wooden textures paired with natural materials',
      'Soft, ambient lighting for a calming environment',
      'Biophilic elements for improved patient comfort',
      'Fluid forms and clean lines enhancing spatial flow',
      'Reception designed as a statement of comfort and professionalism',
      'Thoughtful detailing throughout interiors'
    ],
    spacePlanning: [
      'Efficient flow between reception, waiting, and treatment rooms',
      'Functional workspace layout for doctors and staff',
      'Planned circulation ensuring privacy and smooth navigation',
      'Hygienic design considerations in all treatment zones',
      'Custom-designed furniture to support workflow',
      'Multi-purpose functional elements integrated into layout'
    ],
    interiorDetails: [
      'Custom reception desk with fluid design language',
      'Accent lighting highlighting key interior elements',
      'Subtle wall textures and wooden panel highlights',
      'Modern seating designed for comfort during waiting',
      'Use of natural color palette for warmth and trust',
      'Seamless finishes ensuring easy maintenance'
    ],
    designPhilosophy: [
      'Balance of visual comfort and clinical precision',
      'Spaces designed to reduce anxiety and enhance healing',
      'Human-centered approach to healthcare interiors',
      'Emphasis on durability and easy maintenance',
      'Blending aesthetics with functional workflows',
      'Reflection of SAKAE Studio’s Opulent Architecture principles'
    ]
  },
  client: 'Trudent Dental Clinic',
  year: '2025',
  area: '900 sq ft',
  gallery: Array.from({ length: 8 }, (_, i) =>
    cdnImage(`trudent_v1/x${i + 1}.webp`)
  ),
  designHighlights: [
    'Warm and calming reception environment',
    'Biophilic elements to reduce patient anxiety',
    'Efficient space planning for clinical workflow',
    'Soft ambient and accent lighting',
    'Seamless hygienic finishes',
    'Contemporary healthcare interior identity'
  ]
},

// Project - Trudent Dental Clinic (Version 2 – Detailed)
{
  id: 7,
  title: 'Trudent Dental Clinic – Healthcare Interior Design (Detailed)',
  category: 'Healthcare',
  architect: 'Sameed Ahmed Khan',
  projectType: 'Dental Clinic',
  strategy: 'Comprehensive Interior Design',
  date: 'September 2025',
  status: 'Completed',
  image: cdnImage('trudent_v2/s1.webp'),
  description:
    'A highly detailed interior design project for a modern dental clinic, blending precision, hygiene, and a calming visual identity with contemporary aesthetics.',
  details: {
    overview:
      'Designed with a comprehensive interior planning approach, Trudent Dental Clinic transforms a compact space into an efficient, inviting, and visually balanced healthcare environment.',
    circulationPlanning: [
      'Smooth circulation between reception, consultation, and treatment rooms',
      'Clear visibility for staff while maintaining patient privacy',
      'Optimized functional zoning for workflow efficiency',
      'Ergonomically designed workspaces and dental equipment placement',
      'Accessible storage integrated into treatment zones',
      'Compact yet efficient spatial organization'
    ],
    materialsAndFinishes: [
      'Seamless flooring for hygiene and easy maintenance',
      'High-gloss laminates and antimicrobial surfaces',
      'Color palette of whites, muted greens, and warm wood tones',
      'Materials promoting cleanliness and durability',
      'Minimalist furniture with soft curves',
      'Texture balance that evokes calmness and trust'
    ],
    lightingDesign: [
      'Combination of ambient and task-focused lighting',
      'Precision lighting for treatment accuracy',
      'Soft ambient lighting to reduce anxiety',
      'Illuminated wave wall creating depth and identity',
      'Logo feature wall with accented lighting',
      'Warm tones to enhance comfort'
    ],
    biophilicElements: [
      'Indoor greenery integrated throughout the clinic',
      'Natural wood textures enhancing warmth',
      'Design aimed at reducing patient stress',
      'Soft organic lines throughout spaces',
      'Nature-inspired artwork and accents',
      'Connection to natural materials'
    ],
    interiorIdentity: [
      'Modern lounge-like reception space',
      'Premium yet accessible design language',
      'Balanced aesthetics without clinical harshness',
      'Signature wave wall giving visual continuity',
      'Elegant branding integration',
      'Reflection of modern healthcare design standards'
    ]
  },
  client: 'Trudent Dental Clinic',
  year: '2025',
  area: '900 sq ft',
  gallery: Array.from({ length: 6 }, (_, i) =>
    cdnImage(`trudent_v2/s${i + 1}.webp`)
  ),
  designHighlights: [
    'Clinical functionality blended with aesthetic warmth',
    'Ergonomic space planning',
    'Ambient + precision lighting strategy',
    'Antimicrobial and seamless material selection',
    'Biophilic integrations for patient wellness',
    'Distinct visual identity through feature walls'
  ]
},
{
  id: 8,
  title: 'Luxury Classical Villa',
  category: 'residential',
  architect: 'Sameed Ahmed Khan',
  projectType: 'Luxury Residential Villa',
  strategy: 'Neo-Classical Luxury Design',
  date: '2025',
  status: 'Concept Design & 3D Visualization',
  image: cdnImage('LuxuryClassicalVilla/v1.webp'), // Update image path
  description:
    'A timeless luxury villa blending Neo-Classical European architecture with modern living, featuring grand columns, elegant façades, landscaped gardens, and iconic architectural detailing.',

  details: {
    overview:
      'Designed by Sakae Studio Pvt. Ltd., this Luxury Classical Villa reflects timeless European architecture combined with modern luxury. Every space has been planned to create an iconic residence with grandeur, comfort, and functionality.',

    architecture: [
      'Grand Neo-Classical façade with decorative pediments',
      'Elegant double-height entrance porch',
      'Continuous wrap-around balconies',
      'Classical columns and ornamental cornices',
      'Dedicated external lift tower',
      'Spacious verandahs on every floor'
    ],

    landscapeDesign: [
      'Premium landscaped gardens',
      'Central water fountain feature',
      'Gazebo seating area',
      'Visitor parking with dedicated circulation',
      'Decorative compound wall with premium entrance gate',
      'Large open spaces for natural light and ventilation'
    ],

    projectFeatures: [
      'Iconic architectural elevation',
      'Luxurious arrival experience',
      'Seamless indoor-outdoor connection',
      'Wide driveway with drop-off zone',
      'Premium hardscape and softscape planning',
      'Modern structural planning integrated with classical aesthetics'
    ],

    designPhilosophy: [
      'Inspired by timeless European Classical architecture',
      'Balanced symmetry and elegant proportions',
      'Open layouts maximizing daylight and ventilation',
      'Thoughtfully designed outdoor living spaces',
      'Traditional grandeur combined with modern planning',
      'Prestigious and enduring residential experience'
    ],

    scopeOfWork: [
      'Architectural Planning',
      'Elevation Design',
      'Site Master Planning',
      'Landscape Design',
      'Compound Wall & Gate Design',
      'Exterior Material Selection',
      '3D Photorealistic Visualization',
      'Lighting Concept',
      'Construction Drawings (as applicable)',
      'Project Consultation'
    ]
  },

  client: 'Private Client',
  year: '2025',
  area: 'Luxury Villa',
  gallery: Array.from({ length: 6 }, (_, i) =>
    cdnImage(`LuxuryClassicalVilla/v${i + 1}.webp`)
  ),

  designHighlights: [
    'Neo-Classical European Architecture',
    'Grand Double-Height Entrance',
    'Wrap-Around Balconies',
    'Decorative Columns & Cornices',
    'Premium Landscape Design',
    'Central Water Fountain',
    'Gazebo Seating Area',
    'Dedicated Lift Tower',
    'Luxury Exterior Planning',
    'Photorealistic Architectural Visualization'
  ]
},
{
  id: 9,
  title: 'Corporate Building',
  category: 'commercial',
  architect: 'Sakae Studio Pvt. Ltd.',
  projectType: 'Commercial Office Development',
  strategy: 'Sustainable Workplace Architecture',
  status: 'Concept Design',
  image: require('../assets/CorporateBuilding/Scene 22_3.png'),
  description: 'A contemporary office development with premium workspaces, landscaped public areas, a glazed architectural identity, and sustainable design principles.',
  details: {
    overview: 'Project Corporate Building is envisioned as a dynamic business destination that combines contemporary architecture, flexible workspaces, green vertical elements, and a daylight-rich central atrium.'
  },
  client: 'Confidential',
  year: '2025',
  area: 'Commercial Development',
  gallery: [
    require('../assets/CorporateBuilding/Scene 22_3.png'),
    require('../assets/CorporateBuilding/Scene 30_1.png'),
    require('../assets/CorporateBuilding/Scene 32_1.png'),
    require('../assets/CorporateBuilding/Scene 48_1.png')
  ],
  designHighlights: ['Glazed central atrium', 'Vertical green facade', 'Flexible premium offices', 'Pedestrian-focused planning']
},
{
  id: 10,
  title: 'Modern Kitchen',
  category: 'residential',
  architect: 'Sakae Studio Pvt. Ltd.',
  projectType: 'Contemporary Kitchen Interior',
  strategy: 'Minimal, Functional & Timeless',
  status: 'Completed',
  image: require('../assets/ModernKitchen/Scene 2.png'),
  description: 'A sophisticated residential kitchen that combines clean aesthetics, intelligent storage, premium surfaces, and a seamless everyday cooking experience.',
  details: {
    overview: 'Project Modern Kitchen uses high-gloss cabinetry, premium stone surfaces, warm wood accents, ambient lighting, a breakfast counter, and an integrated utility area to create an elegant culinary space.'
  },
  client: 'Private Client',
  year: '2025',
  area: 'Residential Kitchen',
  gallery: [
    require('../assets/ModernKitchen/Scene 2.png'),
    require('../assets/ModernKitchen/Scene 4.png'),
    require('../assets/ModernKitchen/Scene 6.png'),
    require('../assets/ModernKitchen/Scene 7.png')
  ],
  designHighlights: ['Modular high-gloss cabinetry', 'Breakfast counter', 'Concealed utility space', 'Ambient under-cabinet lighting']
},
{
  id: 11,
  title: 'Shadab Enclave',
  category: 'commercial',
  architect: 'Sakae Studio Pvt. Ltd.',
  projectType: 'Commercial & Residential Development',
  strategy: 'Contemporary Urban Architecture',
  status: 'Concept Design',
  image: require('../assets/ShadabEnclave/s11.png'),
  description: 'A mixed-use Karimnagar development that brings together vibrant retail frontage and naturally lit residential apartments.',
  details: {
    overview: 'Shadab Enclave integrates retail shops on the ground floor with private residential apartments above, with contemporary facade detailing, landscaped balconies, lift accessibility, and dedicated parking.'
  },
  client: 'Private Client',
  year: '2025',
  area: 'Mixed-Use Development',
  gallery: [
    require('../assets/ShadabEnclave/s11.png'),
    require('../assets/ShadabEnclave/Scene 2_4.png'),
    require('../assets/ShadabEnclave/Scene 4_2.png'),
    require('../assets/ShadabEnclave/KARIMANAGAR [ FIRST FLOOR].jpg'),
    require('../assets/ShadabEnclave/karimnagr [ groung floor].jpg')
  ],
  designHighlights: ['Mixed-use planning', 'Premium retail frontage', 'Landscaped balconies', 'Lift and parking access']
},
{
  id: 12,
  title: 'ARK Home',
  category: 'residential',
  architect: 'Sakae Studio Pvt. Ltd.',
  projectType: 'Modern Minimalist Kitchen',
  strategy: 'Intelligent Modular Kitchen Design',
  status: 'Completed',
  image: require('../assets/ArkHome/Image_40.png'),
  description: 'A sophisticated modular kitchen that pairs minimalist aesthetics with intelligent functionality, premium materials, ergonomic planning, and smart storage.',
  details: {
    overview: 'Project ARK Home features high-gloss white cabinetry, black accents, marble-inspired surfaces, warm ambient lighting, and meticulously planned storage for an elegant, efficient kitchen.'
  },
  client: 'Private Client',
  year: '2025',
  area: 'Residential Kitchen',
  gallery: [
    require('../assets/ArkHome/Image_40.png'),
    require('../assets/ArkHome/Image_41.png'),
    require('../assets/ArkHome/Image_42.png'),
    require('../assets/ArkHome/Scene 10_14.png'),
    require('../assets/ArkHome/Scene 11_15.png'),
    require('../assets/ArkHome/Scene 12_17.png'),
    require('../assets/ArkHome/Scene 3_17.png'),
    require('../assets/ArkHome/Scene 4_23.png')
  ],
  designHighlights: ['U-shaped modular layout', 'Smart storage systems', 'Premium marble-look surfaces', 'Integrated appliance planning']
},
{
  id: 13,
  title: 'AYYUB ALI Office',
  category: 'commercial',
  architect: 'Sakae Studio Pvt. Ltd.',
  projectType: 'Executive Office Interior',
  strategy: 'Brand-Focused Executive Workspace',
  status: 'Completed',
  image: require('../assets/AyyubAliOffice/Scene 3_2.png'),
  description: 'A refined executive workspace for Ayyub Ali Fragrances, combining bespoke furniture, premium finishes, and an integrated brand experience.',
  details: {
    overview: 'Project AYYUB ALI Office combines warm wood finishes, textured walls, integrated lighting, bespoke furniture, and a branded feature wall to create a professional, welcoming executive cabin.'
  },
  client: 'Ayyub Ali Fragrances',
  year: '2025',
  area: 'Executive Office',
  gallery: [
    require('../assets/AyyubAliOffice/Scene 3_2.png'),
    require('../assets/AyyubAliOffice/Scene 4_2.png'),
    require('../assets/AyyubAliOffice/Scene 9.png')
  ],
  designHighlights: ['Bespoke executive furniture', 'Illuminated brand feature wall', 'Premium wood and textured finishes', 'Layered ambient lighting']
},


  ]);

  const categories = [
    { key: 'all', label: 'All Projects' },
    { key: 'residential', label: 'Residential' },
    { key: 'commercial', label: 'Commercial' },
    { key: 'renovation', label: 'Renovation' }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  const openModal = (project) => {
    setSelectedProject(project);
    setActiveImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setActiveImageIndex(prev =>
      prev === selectedProject.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex(prev =>
      prev === 0 ? selectedProject.gallery.length - 1 : prev - 1
    );
  };

  // Keyboard nav for lightbox
  useEffect(() => {
    if (!selectedProject) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedProject, activeImageIndex]);

  return (
    <div className="portfolio-container">
      <Header />

      <section className="portfolio-hero">
        <div className="hero-content">
          <p className="hero-eyebrow">Sakae Studio</p>
          <h1>Our Portfolio</h1>
          <p>A curated collection of meticulously crafted spaces — where opulence meets precision</p>
        </div>
      </section>

      <section className="filter-section">
        <div className="container">
          <div className="filter-tabs">
            <Filter size={18} className="filter-icon" />
            {categories.map(category => (
              <button
                key={category.key}
                className={`filter-btn ${selectedCategory === category.key ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.key)}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="projects-grid">
        <div className="container">
          <div className="grid">
            {filteredProjects.map(project => (
              <ProjectCard key={project.id} project={project} onOpen={openModal} />
            ))}
          </div>
        </div>
      </section>

      {selectedProject && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeModal} aria-label="Close">
              <X size={20} />
            </button>

            <div className="modal-header">
              <p className="modal-eyebrow">{selectedProject.category.charAt(0).toUpperCase() + selectedProject.category.slice(1)} — Sakae Studio</p>
              <h2>{selectedProject.title}</h2>
              <div className="project-meta">
                <span><MapPin size={13} />{selectedProject.projectType}</span>
                <span><Maximize2 size={13} />{selectedProject.area}</span>
                <span><Calendar size={13} />{selectedProject.year}</span>
              </div>
            </div>

            <div className="gallery-section">
              <div className="main-image">
                <img
                  key={activeImageIndex}
                  src={selectedProject.gallery[activeImageIndex]}
                  alt={`${selectedProject.title} — View ${activeImageIndex + 1}`}
                  loading="eager"
                  decoding="async"
                />
                <button className="nav-btn prev" onClick={(e) => { e.stopPropagation(); prevImage(); }} aria-label="Previous image">&#8249;</button>
                <button className="nav-btn next" onClick={(e) => { e.stopPropagation(); nextImage(); }} aria-label="Next image">&#8250;</button>
                <div className="image-counter">{activeImageIndex + 1} / {selectedProject.gallery.length}</div>
              </div>
              <div className="thumbnail-container">
                {selectedProject.gallery.map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setActiveImageIndex(index); }}
                  >
                    <img
                      src={img}
                      alt={`View ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="details-section">
              <div className="details-column">
                <div className="detail-group">
                  <h3>Project Details</h3>
                  <div className="detail-item">
                    <strong>Client:</strong> {selectedProject.client}
                  </div>
                  <div className="detail-item">
                    <strong>Architect:</strong> {selectedProject.architect}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong> {selectedProject.status}
                  </div>
                  <div className="detail-item">
                    <strong>Strategy:</strong> {selectedProject.strategy}
                  </div>
                </div>

                <div className="detail-group">
                  <h3>Design Highlights</h3>
                  <div className="highlight-tags">
                    {selectedProject.designHighlights.map((highlight, index) => (
                      <span key={index} className="highlight-tag">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="details-column">
                {selectedProject.details && (
                  <>
                    {/* Dynamic rendering based on project type */}
                    {selectedProject.id === 1 && (
                      <>
                        <div className="detail-group">
                          <h3>Living & Dining</h3>
                          <ul>
                            {selectedProject.details.livingDining.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Bedrooms</h3>
                          <ul>
                            {selectedProject.details.bedrooms.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Kitchen</h3>
                          <ul>
                            {selectedProject.details.kitchen.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}

                    {selectedProject.id === 2 && (
                      <>
                        <div className="detail-group">
                          <h3>Ground Floor</h3>
                          <ul>
                            {selectedProject.details.groundFloor.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>First Floor</h3>
                          <ul>
                            {selectedProject.details.firstFloor.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Exterior Design</h3>
                          <ul>
                            {selectedProject.details.exteriorDesign.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}

                    {selectedProject.id === 3 && (
                      <>
                        <div className="detail-group">
                          <h3>Master Planning</h3>
                          <ul>
                            {selectedProject.details.masterPlanning.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Unit Design</h3>
                          <ul>
                            {selectedProject.details.unitDesign.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Amenities</h3>
                          <ul>
                            {selectedProject.details.amenities.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}

                    {selectedProject.id === 4 && (
                      <>
                        <div className="detail-group">
                          <h3>Conceptualization</h3>
                          <ul>
                            {selectedProject.details.conceptualization.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Living Spaces</h3>
                          <ul>
                            {selectedProject.details.livingSpaces.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Design Elements</h3>
                          <ul>
                            {selectedProject.details.designElements.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}

                    {selectedProject.id === 5 && (
                      <>
                        <div className="detail-group">
                          <h3>Architecture</h3>
                          <ul>
                            {selectedProject.details.architecture.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Site Planning</h3>
                          <ul>
                            {selectedProject.details.sitePlanning.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="detail-group">
                          <h3>Design Philosophy</h3>
                          <ul>
                            {selectedProject.details.designPhilosophy.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}
                    {selectedProject.id === 6 && (
  <>
    <div className="detail-group">
      <h3>Design Concept</h3>
      <ul>
        {selectedProject.details.designConcept.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Space Planning</h3>
      <ul>
        {selectedProject.details.spacePlanning.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Interior Details</h3>
      <ul>
        {selectedProject.details.interiorDetails.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Design Philosophy</h3>
      <ul>
        {selectedProject.details.designPhilosophy.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  </>
)}



{selectedProject.id === 7 && (
  <>
    <div className="detail-group">
      <h3>Circulation Planning</h3>
      <ul>
        {selectedProject.details.circulationPlanning.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Materials & Finishes</h3>
      <ul>
        {selectedProject.details.materialsAndFinishes.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Lighting Design</h3>
      <ul>
        {selectedProject.details.lightingDesign.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Biophilic Elements</h3>
      <ul>
        {selectedProject.details.biophilicElements.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Interior Identity</h3>
      <ul>
        {selectedProject.details.interiorIdentity.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  </>
)}
{selectedProject.id === 8 && (
  <>
    <div className="detail-group">
      <h3>Architecture</h3>
      <ul>
        {selectedProject.details.architecture.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Landscape Design</h3>
      <ul>
        {selectedProject.details.landscapeDesign.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Project Features</h3>
      <ul>
        {selectedProject.details.projectFeatures.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Design Philosophy</h3>
      <ul>
        {selectedProject.details.designPhilosophy.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="detail-group">
      <h3>Scope of Work</h3>
      <ul>
        {selectedProject.details.scopeOfWork.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  </>
)}
                  </>
                )}
              </div>
            </div>

            <div className="project-description">
              <h3>Project Overview</h3>
              <p>{selectedProject.description}</p>
              {selectedProject.details?.overview && (
                <p>{selectedProject.details.overview}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <p className="hero-eyebrow" style={{color:'var(--gold-primary)'}}>Begin Your Journey</p>
            <h2>Inspired by Our Work?</h2>
            <p>Let's collaborate to craft your dream space with opulent precision and timeless elegance.</p>
            <button
              className="primary-btn"
              onClick={() => navigate('/start-project')}
            >
              Start Your Project
            </button>
          </div>
        </div>
      </section>

      <Footer className="footer--home-green" />
      <Chatbot />
    </div>
  );
};

export default HeroPortfolio;
